import { Card, FormControl, FormErrorMessage } from "@chakra-ui/react";
import { Formik } from "formik";
import { isEmpty } from "lodash";
import { FunctionComponent, useCallback } from "react";
import { addressLookup } from "src/components/Boundary/services";
import { useLoadGooglePlacesScript } from "src/hooks/useLoadGoogleMaps";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import {
  AddressValidationSchema,
  EXISTING_ADDRESS,
  ExistingAddress,
  FormAddress,
  FormAddressSchema,
} from "src/schemas/Address";
import { address as formatAddress } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import {
  UpsertUserAddress,
  UpsertUserAddressVariables,
} from "src/types/graphql";
import { UPSERT_USER_ADDRESS } from "../mutations";
import { GET_USER_ADDRESSES } from "../queries";
import { ValidateNewAddressOrchestrator } from "./ValidateNewAddressOrchestrator";

type ValidateNewAddressFormProps = {
  userId: string;
  onAfterSubmit: (upsertedAddress: ExistingAddress) => Promise<void>;
  addressIsRequired?: boolean;
};

const BLANK_ADDRESS = {
  street_address: "",
  street_address_line_2: "",
  city: "",
  state: "",
  zip_code: "",
  geom: null,
};

/**
 * Nested form that appears when "Add new address" button is clicked.
 */
export const ValidateNewAddressForm: FunctionComponent<
  ValidateNewAddressFormProps
> = (props) => {
  useLoadGooglePlacesScript();

  const { userId, onAfterSubmit, addressIsRequired } = props;

  const [upsertAddressBookEntry] = useRemoteDataMutation<
    UpsertUserAddress,
    UpsertUserAddressVariables
  >(UPSERT_USER_ADDRESS, {
    refetchQueries: [GET_USER_ADDRESSES],
  });

  const onSubmitNewValidatedAddress = useCallback(
    async (address: FormAddress) => {
      const formAddress = FormAddressSchema.parse(address);
      try {
        const coordinates = await addressLookup(
          new google.maps.Geocoder(),
          formAddress
        );
        if (coordinates && coordinates.length === 2)
          formAddress.geom = {
            type: "Point",
            coordinates: coordinates as [number, number],
          };
      } catch (e: unknown) {
        formAddress.geom = null;
        console.error(
          `failed to geocode address: ${
            e instanceof Error ? e.message : "unknown"
          }`
        );
      }
      const { data } = await upsertAddressBookEntry({
        variables: {
          user_address: {
            ...formAddress,
            user_id: userId,
          },
        },
      });
      if (!data?.insert_user_address_one) {
        throw new Error(`Failed to upsert address: ${formatAddress(address)}`);
      }

      const upsertedAddress: ExistingAddress = {
        ...formAddress,
        kind: EXISTING_ADDRESS,
        user_address_id: data.insert_user_address_one.id,
        user_id: userId,
      };

      onAfterSubmit(upsertedAddress);
    },
    [upsertAddressBookEntry, userId, onAfterSubmit]
  );

  return (
    <Formik<FormAddress>
      initialValues={BLANK_ADDRESS}
      onSubmit={onSubmitNewValidatedAddress}
      validate={validateWithZod(AddressValidationSchema)}
    >
      {({ errors, touched }) => {
        const isTouched = !isEmpty(touched);
        const hasFieldErrors = isTouched && !isEmpty(errors);
        const hasValidUnsavedChanges = isTouched && isEmpty(errors);
        return (
          <FormControl isInvalid={hasFieldErrors || hasValidUnsavedChanges}>
            <Card
              border={
                hasValidUnsavedChanges ? "2px solid red" : "2px solid white"
              }
            >
              <ValidateNewAddressOrchestrator
                addressIsRequired={addressIsRequired}
              />
            </Card>
            {hasValidUnsavedChanges && (
              <FormErrorMessage>
                There are unsaved address changes.
              </FormErrorMessage>
            )}
          </FormControl>
        );
      }}
    </Formik>
  );
};
